import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardBody, Button, Chip } from '@nextui-org/react'
import { FiDownload } from 'react-icons/fi'
import { BsBank, BsBox } from 'react-icons/bs'
import { FiDollarSign } from 'react-icons/fi'
import Notiflix from 'notiflix'
import { getProjectFinancials, getProjectFinancialSummary } from '../../services/financials'

const OrganizationFinancials = ({ projects }) => {
  const [loading, setLoading] = useState(true)
  const [records, setRecords] = useState([])
  const [summaries, setSummaries] = useState({})

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true)
        const projList = Array.isArray(projects) ? projects : []
        const tasks = projList.map(async (p) => {
          const slug = p.pslug || p.slug || p.project_slug
          if (!slug) return { items: [], summary: null, project: p }
          const [itemsRes, summaryRes] = await Promise.all([
            getProjectFinancials({ slug }),
            getProjectFinancialSummary({ slug }).catch(() => null)
          ])
          return { items: itemsRes?.data || [], summary: summaryRes, project: p }
        })

        const results = await Promise.all(tasks)
        const allItems = []
        const sumMap = {}
        results.forEach(({ items, summary, project }) => {
          const projectName = project.pname || project.title || 'Proje'
          const projectSlug = project.pslug || project.slug
          items.forEach((it) => allItems.push({ ...it, _projectName: projectName, _projectSlug: projectSlug }))
          sumMap[projectSlug || projectName] = summary
        })
        setRecords(allItems)
        setSummaries(sumMap)
      } catch (e) {
        Notiflix.Notify.failure('Finans verileri yüklenemedi')
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
  }, [projects])

  const totals = useMemo(() => {
    const acc = { income: 0, expense: 0, asset: 0, balance: 0 }
    Object.values(summaries).forEach((s) => {
      if (!s) return
      acc.income += s.total_income || 0
      acc.expense += s.total_expense || 0
      acc.asset += s.total_asset || 0
      acc.balance += s.net_balance || 0
    })
    // Eğer özet yoksa kayıtlar üzerinden kabaca hesapla
    if (acc.income === 0 && acc.expense === 0 && acc.asset === 0 && records.length > 0) {
      records.forEach((r) => {
        const amt = parseFloat(r.amount) || 0
        if (r.type === 'income') acc.income += amt
        else if (r.type === 'expense') acc.expense += amt
        else if (r.type === 'asset') acc.asset += amt
      })
      acc.balance = acc.income - acc.expense
    }
    return acc
  }, [summaries, records])

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-5">
          <div className="flex items-start justify-between mb-2">
            <div className="p-2.5 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <BsBank className="text-xl text-green-600 dark:text-green-400" />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Toplam Gelir</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">₺{totals.income.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-5">
          <div className="flex items-start justify-between mb-2">
            <div className="p-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <FiDollarSign className="text-xl text-red-600 dark:text-red-400" />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Toplam Gider</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">₺{totals.expense.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-5">
          <div className="flex items-start justify-between mb-2">
            <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <BsBox className="text-xl text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Demirbaşlar</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">₺{totals.asset.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-400 rounded-xl p-5">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Net Bakiye</p>
          <p className={`text-2xl font-semibold ${totals.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>₺{totals.balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
        </div>
      </div>

      {/* Kayıtlar Tablosu */}
      <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
        <CardBody className="p-0">
          {records.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-400 border-b border-gray-200 dark:border-dark-500">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Proje</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Tür</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Açıklama</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Tutar</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Tarih</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-500">
                  {records.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-dark-400 transition-colors duration-150">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{r._projectName}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{r.type}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.description}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">{parseFloat(r.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {r.currency || 'TRY'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{new Date(r.date).toLocaleDateString('tr-TR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">Bu organizasyona ait finansal kayıt bulunamadı.</p>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default OrganizationFinancials


